import BigNumber from "bignumber.js";

const AED_DP = 2;
const USDT_DP = 2;

function toBn(raw) {
  const bn = new BigNumber(raw == null ? "0" : String(raw).replace(/,/g, ""));
  return bn.isFinite() ? bn : new BigNumber(0);
}

function roundDown(raw, dp) {
  return toBn(raw).toFixed(dp, BigNumber.ROUND_DOWN);
}

function clampDp(raw, fallback = 2) {
  if (raw == null || raw === "") return fallback;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 0 || n > 18) return fallback;
  return n;
}

function aed(raw) {
  return roundDown(raw, AED_DP);
}

function qty(raw, decimals) {
  return roundDown(raw, clampDp(decimals, 8));
}

/** Pick mid for base/quote from rates payload. */
export function findPairMid(rates, baseAsset, quoteAsset) {
  const base = String(baseAsset || "").toUpperCase();
  const quote = String(quoteAsset || "").toUpperCase();
  const pairs = Array.isArray(rates?.pairs) ? rates.pairs : [];
  const row = pairs.find(
    (p) =>
      String(p?.base_asset || "").toUpperCase() === base &&
      String(p?.quote_asset || "").toUpperCase() === quote
  );
  if (!row || row.mid == null || row.mid === "") return null;
  const mid = toBn(row.mid);
  if (!mid.isFinite() || mid.lte(0)) return null;
  return {
    mid: mid.toFixed(),
    source: row.source || null,
    updated_at: row.updated_at || null,
    stale: !!row.stale,
  };
}

function feeScheduleForSide(rates, side) {
  const fees = rates?.fees || {};
  const sideNorm = String(side || "").toUpperCase() === "SELL" ? "SELL" : "BUY";
  return sideNorm === "SELL" ? fees.sell || null : fees.buy || null;
}

function computeAedFee(schedule, baseAedStr) {
  const base = aed(baseAedStr);
  if (!schedule) {
    return {
      fee_aed: "0.00",
      fee_type: "FLAT",
      fee_bps: 0,
      flat_aed: "0.00",
    };
  }
  const feeType =
    String(schedule.fee_type || "FLAT").toUpperCase() === "BPS" ? "BPS" : "FLAT";
  let fee = new BigNumber(0);
  let feeBps = 0;
  let flatAed = "0.00";
  if (feeType === "BPS") {
    feeBps = Number(schedule.bps) || 0;
    if (!Number.isFinite(feeBps) || feeBps < 0) feeBps = 0;
    fee = toBn(base).times(feeBps).div(10000);
  } else {
    flatAed = aed(schedule.flat_aed || "0");
    fee = toBn(flatAed);
  }
  const minFee = schedule.min_fee;
  const maxFee = schedule.max_fee;
  if (minFee != null && minFee !== "") {
    fee = BigNumber.max(fee, toBn(minFee));
  }
  if (maxFee != null && maxFee !== "") {
    fee = BigNumber.min(fee, toBn(maxFee));
  }
  if (!fee.isFinite() || fee.lt(0)) fee = new BigNumber(0);
  return {
    fee_aed: aed(fee),
    fee_type: feeType,
    fee_bps: feeType === "BPS" ? feeBps : 0,
    flat_aed: feeType === "FLAT" ? flatAed : "0.00",
  };
}

function dpForAsset(code, qtyDecimals) {
  const c = String(code || "").toUpperCase();
  if (c === "AED") return AED_DP;
  if (c === "USDT") return USDT_DP;
  return clampDp(qtyDecimals, 8);
}

/**
 * Local preview matching backend convertQuoteService (display only).
 * @param {'spend'|'receive'} [inputField='spend']
 *   spend  — BUY: amount is AED to spend; SELL: amount is crypto to sell
 *   receive — BUY: amount is desired crypto; SELL: amount is desired AED
 * @returns {null|{ side, cmc_rate, you_spend, you_receive, gross_receive, fee_aed, fee_type, fee_bps, flat_aed, convert_aed, amount_aed, amount_crypto, price_source, stale, input_field }}
 */
export function computeConvertPreview({
  side,
  amount,
  rates,
  baseAsset,
  quoteAsset,
  qtyDecimals,
  inputField = "spend",
}) {
  const sideNorm = String(side || "").toUpperCase() === "SELL" ? "SELL" : "BUY";
  const field = String(inputField || "spend").toLowerCase() === "receive" ? "receive" : "spend";
  const midInfo = findPairMid(rates, baseAsset, quoteAsset);
  if (!midInfo) return null;
  const amt = String(amount || "").trim();
  if (!/^\d+(\.\d+)?$/.test(amt) || toBn(amt).lte(0)) return null;

  const mid = toBn(midInfo.mid);
  const cryptoDp = dpForAsset(baseAsset, qtyDecimals);
  const schedule = feeScheduleForSide(rates, sideNorm);

  let spendAmount;
  if (sideNorm === "BUY") {
    if (field === "spend") {
      spendAmount = aed(amt);
    } else {
      // Desired crypto receive → solve AED spend (fee included)
      const wantCrypto = qty(amt, cryptoDp);
      if (toBn(wantCrypto).lte(0)) return null;
      const convertTarget = aed(toBn(wantCrypto).times(mid));
      spendAmount = convertTarget;
      for (let i = 0; i < 10; i += 1) {
        const feeQuote = computeAedFee(schedule, spendAmount);
        const next = aed(toBn(convertTarget).plus(toBn(feeQuote.fee_aed)));
        if (next === spendAmount) break;
        spendAmount = next;
      }
      // Nudge up by 0.01 AED until forward receive covers the typed crypto (rounding)
      for (let i = 0; i < 25; i += 1) {
        const forward = forwardBuy(spendAmount, mid, midInfo, schedule, baseAsset, quoteAsset, cryptoDp);
        if (!forward) break;
        if (toBn(forward.you_receive).gte(toBn(wantCrypto))) {
          return { ...forward, input_field: field };
        }
        spendAmount = aed(toBn(spendAmount).plus("0.01"));
      }
    }
    const out = forwardBuy(spendAmount, mid, midInfo, schedule, baseAsset, quoteAsset, cryptoDp);
    return out ? { ...out, input_field: field } : null;
  }

  // SELL
  if (field === "spend") {
    spendAmount = qty(amt, cryptoDp);
  } else {
    // Desired AED receive → solve crypto spend
    const wantAed = aed(amt);
    if (toBn(wantAed).lte(0)) return null;
    let gross = wantAed;
    for (let i = 0; i < 10; i += 1) {
      const feeQuote = computeAedFee(schedule, gross);
      const next = aed(toBn(wantAed).plus(toBn(feeQuote.fee_aed)));
      if (next === gross) break;
      gross = next;
    }
    spendAmount = qty(toBn(gross).div(mid), cryptoDp);
    for (let i = 0; i < 25; i += 1) {
      const forward = forwardSell(spendAmount, mid, midInfo, schedule, baseAsset, quoteAsset, cryptoDp);
      if (!forward) break;
      if (toBn(forward.you_receive).gte(toBn(wantAed))) {
        return { ...forward, input_field: field };
      }
      // bump by one crypto quantum
      const step = new BigNumber(10).pow(-cryptoDp);
      spendAmount = qty(toBn(spendAmount).plus(step), cryptoDp);
    }
  }
  const out = forwardSell(spendAmount, mid, midInfo, schedule, baseAsset, quoteAsset, cryptoDp);
  return out ? { ...out, input_field: field } : null;
}

function forwardBuy(spendAedRaw, mid, midInfo, schedule, baseAsset, quoteAsset, cryptoDp) {
  const spendAed = aed(spendAedRaw);
  const feeQuote = computeAedFee(schedule, spendAed);
  const convertAedBn = toBn(spendAed).minus(toBn(feeQuote.fee_aed));
  if (!convertAedBn.isFinite() || convertAedBn.lte(0)) return null;
  const convertAed = aed(convertAedBn);
  const cryptoQty = qty(toBn(convertAed).div(mid), cryptoDp);
  if (toBn(cryptoQty).lte(0)) return null;
  const grossCrypto = qty(toBn(spendAed).div(mid), cryptoDp);
  return {
    side: "BUY",
    base_asset: String(baseAsset || "").toUpperCase(),
    quote_asset: String(quoteAsset || "").toUpperCase(),
    cmc_rate: midInfo.mid,
    user_rate: midInfo.mid,
    you_spend: spendAed,
    you_receive: cryptoQty,
    gross_receive: grossCrypto,
    fee_aed: feeQuote.fee_aed,
    fee_type: feeQuote.fee_type,
    fee_bps: feeQuote.fee_bps,
    flat_aed: feeQuote.flat_aed,
    convert_aed: convertAed,
    amount_aed: spendAed,
    amount_crypto: cryptoQty,
    price_source: midInfo.source || "CMC",
    stale: midInfo.stale,
    preview: true,
  };
}

function forwardSell(cryptoQtyRaw, mid, midInfo, schedule, baseAsset, quoteAsset, cryptoDp) {
  const cryptoQty = qty(cryptoQtyRaw, cryptoDp);
  if (toBn(cryptoQty).lte(0)) return null;
  const grossAed = aed(toBn(cryptoQty).times(mid));
  const feeQuote = computeAedFee(schedule, grossAed);
  const receiveBn = toBn(grossAed).minus(toBn(feeQuote.fee_aed));
  if (!receiveBn.isFinite() || receiveBn.lte(0)) return null;
  const receiveAed = aed(receiveBn);
  return {
    side: "SELL",
    base_asset: String(baseAsset || "").toUpperCase(),
    quote_asset: String(quoteAsset || "").toUpperCase(),
    cmc_rate: midInfo.mid,
    user_rate: midInfo.mid,
    you_spend: cryptoQty,
    you_receive: receiveAed,
    gross_receive: grossAed,
    fee_aed: feeQuote.fee_aed,
    fee_type: feeQuote.fee_type,
    fee_bps: feeQuote.fee_bps,
    flat_aed: feeQuote.flat_aed,
    convert_aed: receiveAed,
    amount_aed: receiveAed,
    amount_crypto: cryptoQty,
    price_source: midInfo.source || "CMC",
    stale: midInfo.stale,
    preview: true,
  };
}

export function formatLiveRateLine(rates, baseAsset, quoteAsset) {
  const midInfo = findPairMid(rates, baseAsset, quoteAsset);
  if (!midInfo) return null;
  return {
    text: `1 ${String(baseAsset || "").toUpperCase()} ≈ ${midInfo.mid} ${String(quoteAsset || "").toUpperCase()}`,
    stale: midInfo.stale,
    mid: midInfo.mid,
  };
}
